import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "num", label: "A number", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "A special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); setValid(false); return; }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then((r) => setValid(!!r.data.valid))
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const passed = useMemo(() => RULES.filter((r) => r.test(password)).length, [password]);
  const allPass = passed === RULES.length;
  const strength = ["", "Very weak", "Weak", "Fair", "Good", "Strong"][passed];
  const strengthColor = ["", "bg-destructive", "bg-destructive", "bg-yellow-500", "bg-yellow-500", "bg-emerald-500"][passed];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allPass) { setError("Please meet all password requirements."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      localStorage.removeItem("drelik_token");
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-sm">
        <Logo size="lg" className="mb-8" />
        {checking ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Verifying link…</div>
        ) : done ? (
          <div className="rounded-2xl border border-border bg-card p-8" data-testid="reset-done">
            <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4"><CheckCircle2 className="h-6 w-6 text-emerald-500" /></div>
            <h1 className="text-2xl font-bold tracking-tight">Password updated</h1>
            <p className="text-muted-foreground mt-2 text-sm">Your password has been successfully updated. For your security, all sessions were signed out. Please sign in again.</p>
            <Button className="mt-6 w-full h-11 rounded-lg" onClick={() => navigate("/login")} data-testid="reset-to-login-btn">Go to sign in</Button>
          </div>
        ) : !valid ? (
          <div className="rounded-2xl border border-border bg-card p-8" data-testid="reset-invalid">
            <div className="h-12 w-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-4"><XCircle className="h-6 w-6 text-destructive" /></div>
            <h1 className="text-2xl font-bold tracking-tight">Link expired</h1>
            <p className="text-muted-foreground mt-2 text-sm">This reset link is invalid or has expired. Reset links are single-use and valid for 60 minutes.</p>
            <Link to="/forgot-password"><Button className="mt-6 w-full h-11 rounded-lg" data-testid="reset-request-new-btn">Request a new link</Button></Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold tracking-tight">Set a new password</h1>
            <p className="text-muted-foreground mt-2">Choose a strong password you don't use elsewhere.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <div className="relative">
                  <Input id="password" name="new-password" type={show ? "text" : "password"} autoComplete="new-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••••" className="h-11 pr-10" data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="reset-toggle-visibility" aria-label="Toggle password visibility">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password && (
                  <div className="pt-1" data-testid="reset-strength">
                    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden flex gap-1">
                      {[1,2,3,4,5].map((i) => <div key={i} className={`h-full flex-1 rounded-full transition-colors ${i <= passed ? strengthColor : "bg-muted"}`} />)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">Strength: <span className="font-medium">{strength}</span></p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input id="confirm" name="confirm-password" type={show ? "text" : "password"} autoComplete="new-password" required
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••••••" className="h-11" data-testid="reset-confirm-input" />
              </div>
              <ul className="space-y-1 pt-1">
                {RULES.map((r) => {
                  const ok = r.test(password);
                  return <li key={r.key} className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-600" : "text-muted-foreground"}`}>
                    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5 opacity-40" />} {r.label}
                  </li>;
                })}
              </ul>
              {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={loading || !allPass} className="w-full h-11 rounded-lg" data-testid="reset-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
